import {useCallback, useEffect, useRef, useState, useMemo, useReducer} from "react";
import styled from "styled-components";
import {observer} from "mobx-react";
import {ConfirmWindowV5} from "../../popup/ConfirmWindowV5";
import type {TransformObj} from "../../../../utils/dom/DomUtils";
import {DomUtils, HorizontalAlignment, VerticalAlignment} from "../../../../utils/dom/DomUtils";
import type {Color} from "../../../../generated/api/base";
import {Permission, XyiconFeature} from "../../../../generated/api/base";
import {ReactUtils} from "../../../utils/ReactUtils";
import {TextInputV5} from "../../details/datatypes/TextInputV5";
import {DomPortal} from "../../../modules/abstract/portal/DomPortal";
import DotsHorizontalIcon from "../../icons/dots-horizontal.svg?react";
import ListIcon from "../../icons/list.svg?react";
import FrameIcon from "../../icons/frame.svg?react";
import StarIcon from "../../icons/star.svg?react";
import StarFillIcon from "../../icons/star-fill.svg?react";
import ShareIcon from "../../icons/share-dark.svg?react";
import SwitchIcon from "../../icons/switch-horizontal.svg?react";
import RenameIcon from "../../icons/pen-field.svg?react";
import CloneIcon from "../../icons/clone.svg?react";
import TrashIcon from "../../icons/trash.svg?react";
import GlobeIcon from "../../icons/globe.svg?react";
import UserIcon from "../../icons/user.svg?react";
import PenWithLineIcon from "../../icons/pen-with-line.svg?react";
import ChartIcon from "../../icons/chart-line.svg?react";
import EditIcon from "../../icons/file-pencil-alt.svg?react";
import {FlexCenter, VerticalFlexStyle, baseDistance, fontSize, fontWeight, navBarZIndex, zIndex} from "../../styles/styles";
import {colorPalette} from "../../styles/colorPalette";
import {PopupV5} from "../../popup/PopupV5";
import {IconButtonV5} from "../../interaction/IconButtonV5";
import {DropdownButtonV5} from "../../interaction/DropdownButtonV5";
import type {IDropdownOption} from "../../interaction/DropdownOptionsV5";
import {View} from "../../../../data/models/View";
import {Report} from "../../../../data/models/Report";
import type {ViewActionType} from "../../sharing/GlobalSharingPopup";
import {useAppStore} from "../../../../StateManager";
import {StringUtils} from "../../../../utils/data/string/StringUtils";
import {EventNameForViewsChangeInLocalStorage, getActiveWorkspaceViewTabFromLocalStorage} from "../../topbar/ViewTabsCommon";
import DropletIcon from "../../icons/droplet.svg?react";
import type {IViewFolder} from "../../../../data/models/ViewUtils";
import {NavigationEnum} from "../../../../Enums";
import {ViewColorIconSelectorV5} from "./ViewColorIconSelectorV5";
import type {IViewItemPaletteState} from "./ViewItemPalette";
import {updateViewPaletteColor, updateViewPaletteIcon} from "./ViewItemPalette";
import {ViewElementStyled, getKeyForLocalStorageWorkspaceViewSections, viewMenu} from "./WorkspaceViewCommon";
import type {OnWorkspaceRenameActiveChange, WorkspaceViewType} from "./WorkspaceViewCommon";
import {iconSet, isDefaultViewNavigationIcon} from "./ViewIconLibrary";

interface IViewItemProps {
	readonly view: View | Report;
	readonly type: WorkspaceViewType;
	readonly level?: number;
	readonly searchString: string;
	readonly onViewClick: (view: View | Report) => void;
	readonly onShareClick: (viewItem: View | IViewFolder | Report) => void;
	readonly onGlobalViewActionClick: (view: View | Report, viewActionType: ViewActionType, newName?: string) => void;
	readonly onViewPopupOpenChange: (isOpen: boolean) => void;
	readonly onOpenReportPanel: (report: Report) => void;
	readonly onWorkspaceRenameActiveChange: OnWorkspaceRenameActiveChange;
	readonly forceUpdateParent: () => void;
}

export const ViewItemV5 = observer((props: IViewItemProps) => {
	const appState = useAppStore((state) => state.appState);
	const {
		view,
		type,
		level,
		onViewClick,
		onShareClick,
		onGlobalViewActionClick,
		onViewPopupOpenChange,
		onOpenReportPanel,
		onWorkspaceRenameActiveChange,
		forceUpdateParent,
		searchString,
	} = props;
	const [isInEditMode, setIsInEditMode] = useState<boolean>(false);
	const [isViewEllipsisOpen, setIsViewEllipsisOpen] = useState<boolean>(false);
	const [isDescriptionEditPopupOpen, setIsDescriptionEditPopupOpen] = useState<boolean>(false);
	const [isDescriptionTooltipOpen, setIsDescriptionTooltipOpen] = useState<boolean>(false);
	const [descriptionTooltipTransform, setDescriptionTooltipTransform] = useState<TransformObj>(null);
	const [descriptionValue, setDescriptionValue] = useState<string>("");
	const [isDescriptionSaving, setIsDescriptionSaving] = useState<boolean>(false);
	const [, forceUpdate] = useReducer((x) => x + 1, 0);

	const [isViewColorPaletteOpen, setIsViewColorPaletteOpen] = useState<boolean>(false);
	const [paletteState, setPaletteState] = useState<IViewItemPaletteState | null>(null);
	const _viewNameParent = useRef<HTMLDivElement>(null);
	const _viewItemRef = useRef<HTMLDivElement>(null);
	const _tooltipRef = useRef<HTMLDivElement>(null);
	const _timeOutId = useRef<number>(-1);
	const _originalValuesRef = useRef<{color: string; icon: string} | null>(null);
	const _dropdownButtonRef = useRef<HTMLDivElement>(null);
	const isInEditModeRef = useRef<boolean>(isInEditMode);
	const hasReportedEditModeRef = useRef<boolean>(false);
	const isReport = useMemo(() => view.ownFeature === XyiconFeature.Report, [view.ownFeature]);

	const onClick = (e: React.MouseEvent) => {
		// Without this, the navigation panel can open up when you click on a view/folder in a popup
		e.stopPropagation();

		if (_viewItemRef.current && _viewItemRef.current.contains(e.target as Node)) {
			onViewClick(view);
		}
	};

	const onRenameClick = () => {
		// Workaround for focusloss/blur problem
		requestAnimationFrame(() => {
			if (!isInEditMode) {
				setIsInEditMode(true);
			}
		});
	};

	const getErrorMessage = (value: string) => {
		const isNameValid = appState.actions.isNameValidForViewOrReport(value, view.itemFeature, view.id, isReport);

		if (isNameValid) {
			return "";
		}
		return value.trim() === "" ? "Name cannot be empty!" : "Name needs to be unique!";
	};

	const onRenameGlobalViewApply = (value: string) => {
		const isNameValid = appState.actions.isNameValidForViewOrReport(value, view.itemFeature, view.id, isReport);

		if (isNameValid) {
			onBlurRename();

			if (value !== view.name) {
				onGlobalViewActionClick(view, "rename", value);
			}
		}
	};

	const onRenameApply = async (value: string) => {
		const isNameValid = appState.actions.isNameValidForViewOrReport(value, view.itemFeature, view.id, isReport);

		if (isNameValid) {
			onBlurRename();

			if (value !== view.name) {
				view.name = value;
				window.dispatchEvent(new Event(EventNameForViewsChangeInLocalStorage));
				await view.update();
			}
		}
	};

	const onBlurRename = () => {
		if (isInEditMode) {
			setIsInEditMode(false);
		}
	};

	const onDeleteClick = async () => {
		if (type === "Global" || view.data.isGlobal) {
			onGlobalViewActionClick(view, "delete");
		} else {
			const confirmed = await ConfirmWindowV5.open("Are you sure you want to delete the selected 1 item?");

			if (confirmed) {
				const isFavorite = view?.isFavorite;
				await appState.app.transport.services.view.onModifyActiveView([view.id], "deleted");
				await view.delete([view]);

				if (isFavorite) {
					if (appState.user) {
						await appState.user.setFavoriteViews(appState.user.favoriteViews);
					}
				}
			}
		}
	};

	const onEditClick = () => {
		(view as Report).startEditing();
		onOpenReportPanel(view as Report);
	};

	const onDuplicateClick = async () => {
		const duplicate = await view.duplicate();

		if (isReport) {
			appState.user?.updateViewFolderStructures();
			onOpenReportPanel(duplicate as Report);
			forceUpdateParent();
		}

		const keyForLocalStorage = getKeyForLocalStorageWorkspaceViewSections(appState.user.id);
		let openViewSpaceTypes: WorkspaceViewType[] = JSON.parse(localStorage.getItem(keyForLocalStorage)) || null;

		if (!openViewSpaceTypes) {
			openViewSpaceTypes = ["Favorites", "Global", "My Views", "Shared With Me"];
			localStorage.setItem(keyForLocalStorage, JSON.stringify(openViewSpaceTypes));
		}

		const newOpenViewSpaceTypes = new Set([...openViewSpaceTypes]);

		if (type === "Global") {
			if (!newOpenViewSpaceTypes.has("My Views")) {
				newOpenViewSpaceTypes.add("My Views");
			}
			localStorage.setItem(keyForLocalStorage, JSON.stringify([...newOpenViewSpaceTypes]));
			forceUpdateParent();
		}

		return duplicate;
	};

	const onShareItem = () => {
		onShareClick(view);
	};

	const onMarkAsGlobalClick = () => {
		onGlobalViewActionClick(view, "makeGlobal");
	};

	const onMarkAsPrivateClick = () => {
		onGlobalViewActionClick(view, "makePrivate");
	};

	const viewIconColorPaletteOpen = () => {
		const currentColor = view.additionalDetails.navigationPanelData?.color || view.navigationPanelColor;
		const currentIcon = view.additionalDetails.navigationPanelData?.icon || view.navigationPanelIcon;

		_originalValuesRef.current = {
			color: currentColor || "",
			icon: currentIcon || "",
		};

		setPaletteState({
			color: currentColor
				? {
						hex: currentColor,
						transparency: 0,
					}
				: null,
			icon: currentIcon || null,
		});
		setIsViewColorPaletteOpen(true);
		onViewPopupOpenChange(true);
	};

	const viewIconColorPaletteClose = () => {
		if (!paletteState) {
			setIsViewColorPaletteOpen(false);
			onViewPopupOpenChange(false);
			return;
		}

		const currentColor = view.additionalDetails.navigationPanelData?.color || "";
		const currentIcon = view.additionalDetails.navigationPanelData?.icon || "";
		const original = _originalValuesRef.current;

		setPaletteState(null);
		_originalValuesRef.current = null;
		setIsViewColorPaletteOpen(false);
		onViewPopupOpenChange(false);

		if (original && (currentColor !== original.color || currentIcon !== original.icon)) {
			window.dispatchEvent(new Event(EventNameForViewsChangeInLocalStorage));
			view.update().catch((error) => {
				console.error("Failed to update view color/icon:", error);
			});
		}
	};

	const onBackgroundColorChange = (newColor: Color) => {
		updateViewPaletteColor(view, setPaletteState, newColor);
	};

	const handleIconClick = (iconName: string) => {
		updateViewPaletteIcon(view, setPaletteState, iconName);
	};

	const setFavorite = (value: boolean) => {
		view.setFavorite(value);
	};

	const getViewItemPermission = (view: Report | View): Permission => {
		let permission = Permission.None;

		if (view instanceof Report) {
			permission = Report.getPermission(view, appState);
		}

		if (view instanceof View) {
			permission = view.getPermission(appState.user.id);
		}

		return permission;
	};

	const getOptions = (): IDropdownOption[] => {
		const user = appState.user;
		const viewType: WorkspaceViewType = view.isGlobal ? "Global" : view.ownedBy === user.id ? "My Views" : "Shared With Me";
		const permission = getViewItemPermission(view);

		const shouldShowFavoriteSeparator =
			viewType === "My Views" ||
			(viewType === "Shared With Me" && permission >= Permission.Update) ||
			(viewType === "Global" &&
				((permission >= Permission.Update && (user.isAdmin || !isReport)) || (!user.isAdmin && permission >= Permission.View && !isReport)));

		const dropdownOptions: IDropdownOption[] = [
			{
				label: view.isFavorite ? "Remove Favorite" : "Favorite",
				onClick: () => setFavorite(!view.isFavorite),
				IconComponent: view.isFavorite ? StarFillIcon : StarIcon,
				isSeparator: shouldShowFavoriteSeparator,
			},
		];

		switch (viewType) {
			case "My Views":
				dropdownOptions.push(
					...([
						// Color & Icon
						(user.isAdmin || view.ownedBy === user.id) && {
							label: "Color & Icon",
							onClick: viewIconColorPaletteOpen,
							IconComponent: DropletIcon,
							iconCustomStyle: {
								strokeWidth: "2px",
							},
						},

						{
							label: "Rename",
							onClick: onRenameClick,
							IconComponent: RenameIcon,
						},

						{
							label: view.description ? "Edit Description" : "Add Description",
							onClick: onDescriptionEditPopupOpen,
							IconComponent: EditIcon,
						},

						isReport && {
							label: "Edit",
							onClick: onEditClick,
							IconComponent: PenWithLineIcon,
						},

						{
							label: "Duplicate",
							onClick: onDuplicateClick,
							IconComponent: CloneIcon,
							isSeparator: true,
						},
						{
							label: "Share",
							onClick: onShareItem,
							IconComponent: ShareIcon,
						},

						user.isAdmin && {
							label: "Mark as Global View",
							onClick: onMarkAsGlobalClick,
							IconComponent: GlobeIcon,
						},

						{
							label: "Change Owner",
							onClick: () => console.log("onChangeOwnerClick"),
							IconComponent: SwitchIcon,
							isSeparator: true,
						},

						{
							label: "Delete",
							onClick: onDeleteClick,
							IconComponent: TrashIcon,
						},
					].filter(Boolean) as IDropdownOption[]),
				);
				break;
			case "Shared With Me":
				if (permission >= Permission.Update) {
					dropdownOptions.push(
						...([
							{
								label: "Color & Icon",
								onClick: viewIconColorPaletteOpen,
								IconComponent: DropletIcon,
								iconCustomStyle: {
									strokeWidth: "2px",
								},
							},

							{
								label: "Rename",
								onClick: onRenameClick,
								IconComponent: RenameIcon,
							},

							{
								label: view.description ? "Edit Description" : "Add Description",
								onClick: onDescriptionEditPopupOpen,
								IconComponent: EditIcon,
							},

							isReport && {
								label: "Edit",
								onClick: onEditClick,
								IconComponent: PenWithLineIcon,
							},

							(!isReport || (isReport && user.isAdmin)) && {
								label: "Duplicate",
								onClick: onDuplicateClick,
								IconComponent: CloneIcon,
								isSeparator: true,
							},

							{
								label: "Share",
								onClick: onShareItem,
								IconComponent: ShareIcon,
							},

							user.isAdmin && {
								label: "Mark as Global View",
								onClick: onMarkAsGlobalClick,
								IconComponent: GlobeIcon,
							},
						].filter(Boolean) as IDropdownOption[]),
					);
				} else {
					dropdownOptions.push(
						...([
							(!isReport || (isReport && user.isAdmin)) && {
								label: "Duplicate",
								onClick: onDuplicateClick,
								IconComponent: CloneIcon,
							},
						].filter(Boolean) as IDropdownOption[]),
					);
				}

				break;
			case "Global":
				if (user.isAdmin) {
					dropdownOptions.push(
						...([
							(user.isAdmin || view.ownedBy === user.id) && {
								label: "Color & Icon",
								onClick: viewIconColorPaletteOpen,
								IconComponent: DropletIcon,
								iconCustomStyle: {
									strokeWidth: "2px",
								},
							},

							{
								label: "Rename",
								onClick: onRenameClick,
								IconComponent: RenameIcon,
							},

							{
								label: view.description ? "Edit Description" : "Add Description",
								onClick: onDescriptionEditPopupOpen,
								IconComponent: EditIcon,
							},

							isReport && {
								label: "Edit",
								onClick: onEditClick,
								IconComponent: PenWithLineIcon,
							},

							{
								label: "Duplicate",
								onClick: onDuplicateClick,
								IconComponent: CloneIcon,
								isSeparator: true,
							},

							{
								label: "Share",
								onClick: onShareItem,
								IconComponent: ShareIcon,
							},

							{
								label: "Convert to Private View",
								onClick: onMarkAsPrivateClick,
								IconComponent: UserIcon,
								isSeparator: true,
							},

							{
								label: "Delete",
								onClick: onDeleteClick,
								IconComponent: TrashIcon,
							},
						].filter(Boolean) as IDropdownOption[]),
					);
				} else if (permission >= Permission.Update) {
					dropdownOptions.push(
						...([
							{
								label: view.description ? "Edit Description" : "Add Description",
								onClick: onDescriptionEditPopupOpen,
								IconComponent: EditIcon,
							},

							!isReport && {
								label: "Duplicate",
								onClick: onDuplicateClick,
								IconComponent: CloneIcon,
								isSeparator: true,
							},

							{
								label: "Share",
								onClick: onShareItem,
								IconComponent: ShareIcon,
							},
						].filter(Boolean) as IDropdownOption[]),
					);
				} else {
					dropdownOptions.push(
						...([
							!isReport && {
								label: "Duplicate",
								onClick: onDuplicateClick,
								IconComponent: CloneIcon,
							},
						].filter(Boolean) as IDropdownOption[]),
					);
				}

				break;
		}

		return dropdownOptions;
	};

	useEffect(() => {
		if (isDescriptionTooltipOpen && _tooltipRef.current && _viewItemRef.current) {
			const transform = DomUtils.getFixedFloatingElementPosition(
				_viewItemRef.current,
				_tooltipRef.current,
				VerticalAlignment.center,
				HorizontalAlignment.outerRight,
				0,
				0,
			);
			setDescriptionTooltipTransform(transform);
		}
	}, [isDescriptionTooltipOpen]);

	useEffect(() => {
		setDescriptionValue(view.description || "");
	}, [view]);

	useEffect(() => {
		return () => {
			if (isDescriptionEditPopupOpen || isViewColorPaletteOpen) {
				onViewPopupOpenChange(false);
			}
		};
	}, [isDescriptionEditPopupOpen, isViewColorPaletteOpen, onViewPopupOpenChange]);

	useEffect(() => {
		isInEditModeRef.current = isInEditMode;

		if (isInEditMode || hasReportedEditModeRef.current) {
			onWorkspaceRenameActiveChange(isInEditMode);
			hasReportedEditModeRef.current = isInEditMode;
		}
	}, [isInEditMode, onWorkspaceRenameActiveChange]);

	useEffect(() => {
		return () => {
			if (isInEditModeRef.current) {
				onWorkspaceRenameActiveChange(false);
			}
		};
	}, [onWorkspaceRenameActiveChange]);

	const handleViewsChange = useCallback(() => {
		forceUpdateParent();
	}, [forceUpdateParent]);

	useEffect(() => {
		window.addEventListener(EventNameForViewsChangeInLocalStorage, handleViewsChange);

		return () => {
			window.removeEventListener(EventNameForViewsChangeInLocalStorage, handleViewsChange);
		};
	}, [handleViewsChange]);

	const inlineStyleForTooltip: React.CSSProperties = _viewItemRef.current && {
		transform: descriptionTooltipTransform?.translate,
	};

	const onDescriptionEditPopupClose = (e: React.MouseEvent) => {
		if (e) {
			e.stopPropagation();
		}

		closeDescriptionEditPopup();
		setDescriptionValue(view.description);
	};

	const closeDescriptionEditPopup = () => {
		setIsDescriptionEditPopupOpen(false);
		onViewPopupOpenChange(false);
	};

	const onDescriptionEditPopupOpen = () => {
		onViewPopupOpenChange(true);
		setIsDescriptionEditPopupOpen(true);
	};

	const onDescriptionTooltipClose = () => {
		clearTimeout(_timeOutId.current);
		setIsDescriptionTooltipOpen(false);
	};

	const onDescriptionTooltipOpen = () => {
		if (!isDescriptionTooltipOpen) {
			clearTimeout(_timeOutId.current);
			_timeOutId.current = window.setTimeout(() => setIsDescriptionTooltipOpen(true), 200);
		}
	};

	const onViewDescriptionSave = async (e: React.MouseEvent) => {
		e.stopPropagation();

		if (descriptionValue !== view.description) {
			view.description = descriptionValue;
			setIsDescriptionSaving(true);
			await view.update();
			setIsDescriptionSaving(false);
		}

		closeDescriptionEditPopup();
	};

	const onEditDescriptionInput = (value: string) => {
		setDescriptionValue(value);
	};

	useEffect(() => {
		window.addEventListener(EventNameForViewsChangeInLocalStorage, forceUpdate);

		return () => {
			window.removeEventListener(EventNameForViewsChangeInLocalStorage, forceUpdate);
		};
	}, [appState?.user?.id, appState.organizationIdByAuthData]);

	const activeViewTab = getActiveWorkspaceViewTabFromLocalStorage(appState.user.id, appState.organizationIdByAuthData);
	const isActive =
		[NavigationEnum.XYICONS, NavigationEnum.BOUNDARIES, NavigationEnum.REPORT_VIEWS, NavigationEnum.SPACE].includes(appState.selectedMenu) &&
		activeViewTab?.viewId === view.id &&
		((isReport && view?.ownFeature === appState.selectedFeature) || (!isReport && view?.itemFeature === appState.selectedFeature));
	const Icon = isReport ? ChartIcon : view?.itemFeature === XyiconFeature.SpaceEditor ? FrameIcon : ListIcon;
	const TypeIcon = viewMenu.find((vm) => vm.label === type).icon;
	const isFilteredOut = searchString && !StringUtils.containsIgnoreCase(view.name, searchString);

	const iconName = view.additionalDetails.navigationPanelData?.icon;
	const iconColor = view.additionalDetails.navigationPanelData?.color;
	const matchingIcon = !isDefaultViewNavigationIcon(iconName) ? iconSet.find((icon) => icon.label === iconName)?.IconComponent : undefined;
	const NavigationIcon = matchingIcon || Icon;

	if (isFilteredOut) {
		return <></>;
	}

	return (
		<ViewElementStyled
			className={ReactUtils.cls("ViewItem hbox alignCenter", {
				isActive,
				isInEditMode: isInEditMode,
				level1: level === 1,
			})}
			onClick={onClick}
			$isActive={isActive}
			ref={_viewItemRef}
			$isHovered={isViewEllipsisOpen || isViewColorPaletteOpen}
			$iconColor={iconColor}
		>
			<NavigationIcon className="navIcon" />
			{isInEditMode ? (
				<TextInputV5
					className="viewName flex_1"
					value={view.name ?? ""}
					onChange={view.isGlobal ? onRenameGlobalViewApply : onRenameApply}
					onBlur={onBlurRename}
					autoFocus={true}
					getErrorMessage={getErrorMessage}
				/>
			) : (
				<div
					ref={_viewNameParent}
					className="viewName flex_1"
					onMouseOver={!!view && onDescriptionTooltipOpen}
					onMouseLeave={!!view && onDescriptionTooltipClose}
				>
					{view?.name}
				</div>
			)}
			{isDescriptionTooltipOpen && !!view.description && (
				<DomPortal destination={appState.app.modalContainer}>
					<ViewTooltipStyled
						ref={_tooltipRef}
						style={inlineStyleForTooltip}
					>
						<h4>{view.name}</h4>
						<p style={{whiteSpace: "pre-line"}}>{view.description}</p>
						<FlexCenter $gap="4px">
							<TypeIcon />
							<span>{type}</span>
						</FlexCenter>
						{type === "Shared With Me" && (
							<span style={{fontWeight: 400}}>
								<b>Owner: </b>
								{appState.actions.findUser(view.ownedBy)?.fullName || "N/A"}
							</span>
						)}
					</ViewTooltipStyled>
				</DomPortal>
			)}
			{isDescriptionEditPopupOpen && (
				<PopupV5
					onClose={onDescriptionEditPopupClose}
					label="View Description"
					width="400px"
					parentRef={_viewItemRef.current}
					buttonProps={{
						label: isDescriptionSaving ? "Saving..." : "Save",
						onClick: onViewDescriptionSave,
						hideIcon: true,
						disabled: isDescriptionSaving || descriptionValue === view.description,
					}}
					style={{zIndex: 7000, visibility: "visible"}}
					className="edit-view-description-popup"
					isSmallPopup={true}
					horizontalAlignment={HorizontalAlignment.outerRight}
					verticalAlignment={VerticalAlignment.bottomOuter}
					offsetX={-216}
					offsetY={-2}
					arrowIndicator="top-center"
				>
					<TextInputV5
						value={descriptionValue}
						onInput={onEditDescriptionInput}
						isTextArea={true}
						autoFocus={true}
						style={{height: 100}}
						placeholder="Add Description"
					/>
				</PopupV5>
			)}
			{isViewColorPaletteOpen && (
				<ViewColorIconSelectorV5
					view={view}
					color={
						paletteState?.color ?? {
							hex: view.additionalDetails.navigationPanelData?.color || view.navigationPanelColor || colorPalette.gray.c950,
							transparency: 0,
						}
					}
					onColorChange={onBackgroundColorChange}
					onIconClick={handleIconClick}
					onCloseClick={viewIconColorPaletteClose}
					isOpen={isViewColorPaletteOpen}
					elementDivRef={_dropdownButtonRef}
					horizontalAlignment={HorizontalAlignment.outerRight}
					verticalAlignment={VerticalAlignment.bottom}
					offsetY={32}
					offsetX={-28}
					panelWidth={272}
				/>
			)}
			<div ref={_dropdownButtonRef}>
				<DropdownButtonV5
					options={getOptions()}
					button={
						<IconButtonV5
							IconComponent={DotsHorizontalIcon}
							onClick={() => {
								if (isViewColorPaletteOpen) {
									viewIconColorPaletteClose();
								}
								setIsViewEllipsisOpen((prevState) => !prevState);
							}}
						/>
					}
					optionsZIndex={navBarZIndex + 1}
					onOpenChange={(isOpen: boolean) => {
						if (!isOpen) {
							setIsViewEllipsisOpen(false);
						}
					}}
					className="viewItem"
				/>
			</div>
		</ViewElementStyled>
	);
});

export const ViewTooltipStyled = styled.div`
	${VerticalFlexStyle};
	gap: ${baseDistance.xs};
	position: absolute;
	background-color: ${colorPalette.gray.c900};
	color: ${colorPalette.white};
	padding: ${baseDistance.sm};
	width: 300px;
	border-radius: ${baseDistance.xs};
	z-index: ${zIndex.viewTooltip};
	left: ${baseDistance.sm};
	font-size: ${fontSize.sm};
	line-height: ${fontSize.lg};
	word-wrap: break-word;
	text-align: justify;

	svg {
		width: ${baseDistance.md};
		height: ${baseDistance.md};
	}

	span {
		font-weight: ${fontWeight.bold};
	}

	&::before {
		content: "";
		width: 0;
		height: 0;
		position: absolute;
		border-width: 6px 6px 6px 0;
		border-color: transparent ${colorPalette.gray.c900} transparent ${colorPalette.gray.c900};
		border-style: solid;
		left: -6px;
		top: 50%;
		transform: translateY(-50%);
	}
`;
